import { AuthStatus } from '@amzn/bf-playback'
import { createLogPrefix, ErrorLevel, logDebug, onLine, ReportableError } from '@amazon/vinyl-util'
import type { AuthTokenProvider } from '@amzn/vinyl-amzn'
import { handleError } from '@/errorHandler'
import { TokenAuth } from '@/auth/TokenAuth'
import {
    DESKTOP_SIGNED_OUT,
    desktopShellRef,
    type DesktopSession,
    type DesktopSignInOutcome,
    type SignInEmbed,
} from '@/device/desktopShell'

/**
 * The desktop app's session: the shell runs Auth Portal and registers the device with Panda (the
 * signed-out screen, `DesktopSignInScreen`, drives it), and every request's token comes from the
 * shell, which keeps the refresh token in the OS secure store.
 */
export class DesktopAuth extends TokenAuth {
    logPrefix = createLogPrefix('DesktopAuth')

    constructor() {
        super(AuthStatus.PENDING)
        desktopShellRef.value
            .restore()
            .then(this.settle)
            .catch((error: unknown) => {
                this.reportError(error)
                this.settle(null)
            })
    }

    signIn(): void {
        logDebug(this, 'signIn: the signed-out screen runs it')
    }

    /** Runs the shell's sign-in (`DesktopShell.signIn`); a signed-in outcome starts the session. */
    async signInOn(
        marketplace: string,
        verifyHome: boolean,
        embed: SignInEmbed | null
    ): Promise<DesktopSignInOutcome> {
        logDebug(this, 'signInOn', marketplace)
        const outcome = await desktopShellRef.value.signIn(marketplace, verifyHome, embed)
        if (outcome.outcome === 'signedIn') this.settle(outcome.session)
        return outcome
    }

    // Signed out only once the shell has dropped its session, so a reload or relaunch that follows
    // can't restore it.
    protected async dropSession(): Promise<void> {
        await desktopShellRef.value.signOut().catch(this.reportError)
        this.settle(null)
    }

    private reportError = (error: unknown) => handleError(this, error)

    private settle = (session: DesktopSession | null) => {
        this._authTokenProvider = session ? this.vendToken : null
        this.setStatus(session ? AuthStatus.SIGNED_IN : AuthStatus.SIGNED_OUT)
    }

    private vendToken: AuthTokenProvider = async () => {
        try {
            return await desktopShellRef.value.token()
        } catch (error) {
            // The shell ended the session (Panda refused its refresh token): follow it.
            if (error === DESKTOP_SIGNED_OUT && this.isSignedIn()) {
                this.signOut().catch(this.reportError)
            }
            // Offline the shell can't refresh the token: that is being offline, not a failure.
            if (error !== DESKTOP_SIGNED_OUT && !onLine()) {
                throw new ReportableError(
                    `desktop token: offline: ${String(error)}`,
                    undefined,
                    ErrorLevel.SILENT
                )
            }
            throw new Error(`desktop token: ${String(error)}`)
        }
    }
}
